import { Button, useTheme } from "@mui/material";
import { ActivityIndicator } from "react-native";

interface Props {
	onClick?: (e: any) => void;
	title: string;
	style?: {};
	variant?: "text" | "contained" | "outlined";
	type?: "submit" | "reset" | "button";
	sx?: {};
	size?: "small" | "medium" | "large";
	isLoading?: boolean;
	disabled?: boolean;
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
	children?: React.ReactNode;
}

export const BasicButton = ({
	onClick = () => { },
	title,
	style,
	variant = "contained",
	type = "button",
	sx = {},
	size = "medium",
	isLoading = false,
	disabled = false,
	startIcon = null,
	endIcon = null,
	children = null,
}: Props) => {
	const theme = useTheme();
	const isDark = theme.palette.mode === 'dark';

	return (
		<Button
			type={type}
			sx={{
				textTransform: 'none',
				fontSize: 13,
				fontWeight: 600,
				letterSpacing: 0.3,
				fontFamily: 'primaryFont',
				borderRadius: 1.5,
				px: 2.5,
				py: size === 'small' ? 0.75 : 1,
				...(variant === 'contained' && {
					backgroundColor: theme.palette.primary.main,
					color: '#fff',
					boxShadow: isDark
						? '0 2px 8px rgba(0, 0, 0, 0.4)'
						: '0 2px 8px rgba(0, 0, 0, 0.12)',
					'&:hover': {
						backgroundColor: theme.palette.primary.dark,
						boxShadow: isDark
							? '0 4px 12px rgba(0, 0, 0, 0.5)'
							: '0 4px 12px rgba(0, 0, 0, 0.18)',
					},
					'&:active': {
						boxShadow: isDark
							? '0 1px 4px rgba(0, 0, 0, 0.4)'
							: '0 1px 4px rgba(0, 0, 0, 0.12)',
					},
				}),
				...(variant === 'outlined' && {
					border: `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.23)' : theme.palette.divider}`,
					color: isDark ? theme.palette.text.primary : theme.palette.text.secondary,
					backgroundColor: isDark
						? 'rgba(255, 255, 255, 0.05)'
						: 'transparent',
					'&:hover': {
						backgroundColor: isDark
							? 'rgba(255, 255, 255, 0.08)'
							: 'rgba(0, 0, 0, 0.04)',
						borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : theme.palette.text.secondary,
					},
					'&:active': {
						backgroundColor: isDark
							? 'rgba(255, 255, 255, 0.12)'
							: 'rgba(0, 0, 0, 0.08)',
					},
				}),
				...(variant === 'text' && {
					backgroundColor: 'transparent',
					color: theme.palette.primary.main,
					'&:hover': {
						backgroundColor: isDark
							? 'rgba(255, 255, 255, 0.05)'
							: 'rgba(0, 0, 0, 0.04)',
					},
				}),
				'&.Mui-disabled': {
					opacity: 0.5,
				},
				...sx,
			}}
			style={style}
			variant={variant}
			onClick={onClick}
			disabled={isLoading || disabled}
			startIcon={startIcon}
			endIcon={endIcon}
			size={size}
		>
			{
				isLoading ?
					<ActivityIndicator
						size="small"
						color={variant === 'contained' ? 'white' : theme.palette.primary.main}
					/>
					:
					title
			}
			{children}
		</Button>
	);
};
